import { useReducer } from 'react';
import { defaultSort, getMapRangeFromSortKey } from '../utils/sorting';
import { getDefaultRange, getChartOptionFromMapRange } from '../utils/chartMapSync';
import { decodeRateViewModeFromUrl, isLocationTypeAggregateLocation } from '../utils/urlRouting';
import { getCrimeDisplayName } from '../constants/crimes';
import { getStateAbbreviation } from '../constants/locations';

const initialAppState = {
  selectedCrime: 'all_crime',
  selectedDateRange: '2024',
  selectedSource: 'FBI_CIUS',
  activeTab: 'reports',
  selectedView: 'table',
  isHistorical: false,
  compareMode: 'state',
  rateViewMode: {
    rateType: 'county',
    rateScope: 'state',
  },
  mapRange: undefined,
  chartOption: undefined,
  populationGroup: 'all',
  sortConfig: defaultSort('all_crime')['reports'],
  currentPage: 1,
  itemsPerPage: 20,
};

function resolveRateViewValue(state, value) {
  return typeof value === 'function' ? value(state.rateViewMode) : value;
}

function getDefaultRateViewModeForLocation(locationInfo) {
  const locationType = locationInfo?.type;
  const locationName = locationInfo?.name;
  const isNationalAllCountyCity =
    (locationType === 'county' && locationName === 'All Counties') ||
    (locationType === 'city' && locationName === 'All Cities');

  if (locationType === 'county' || locationType === 'city') {
    return {
      rateType: locationType,
      rateScope: isNationalAllCountyCity ? 'national' : 'state',
    };
  }

  if (locationType === 'metro') {
    return {
      rateType: 'state',
      rateScope: 'state',
    };
  }

  return initialAppState.rateViewMode;
}

function getDefaultCompareModeForLocation(locationInfo, rateViewMode) {
  const locationType = locationInfo?.type;
  const locationName = locationInfo?.name;
  const isCountyOrCity = locationType === 'county' || locationType === 'city';
  const isMetro = locationType === 'metro';

  if (isCountyOrCity || isMetro) {
    if (rateViewMode?.rateScope) return rateViewMode.rateScope;
    if (
      (locationType === 'county' && locationName === 'All Counties') ||
      (locationType === 'city' && locationName === 'All Cities')
    ) {
      return 'national';
    }
  }

  return 'state';
}

/**
 * Compute all 12 URL-derived state fields atomically from a parsed URL.
 * This is the single source of truth for POP navigation and initial URL load.
 */
function deriveStateFromUrl(parsed, locationInfo, prevState) {
  const lt = locationInfo?.type;
  const locationName = locationInfo?.name;
  const isCountyOrCity = lt === 'county' || lt === 'city';
  const isMetro = lt === 'metro';

  // 1. Simple fields
  const selectedCrime = parsed.crime;
  const selectedDateRange = parsed.year;
  const selectedSource = parsed.source;
  const activeTab = parsed.tab;
  const selectedView = parsed.view;
  const isHistorical = !!parsed.historical;
  const populationGroup = parsed.popGroup || 'all';

  // 2. Rate view mode
  let rateViewMode = getDefaultRateViewModeForLocation(locationInfo);
  const hasCountyCityRateParams =
    isCountyOrCity &&
    parsed.tab === 'rates' &&
    (parsed.rateType || parsed.compareScope);
  if (
    (hasCountyCityRateParams || (!isCountyOrCity && parsed.rateType)) &&
    (isCountyOrCity || isMetro || parsed.tab === 'rates')
  ) {
    const decoded = decodeRateViewModeFromUrl({
      locationType: lt,
      rateType: parsed.rateType,
      compareScope: parsed.compareScope,
      locationName,
    });
    if (decoded) rateViewMode = decoded;
  }

  // 3. Compare mode — restore from URL for all location types
  let compareMode;
  if (parsed.compareScope) {
    compareMode = parsed.compareScope;
  } else {
    compareMode = getDefaultCompareModeForLocation(locationInfo, rateViewMode);
  }

  // 4. Sort config
  let sortConfig;
  const isAgg = isLocationTypeAggregateLocation(locationInfo);
  if (isAgg && parsed.tableSort && parsed.tab !== 'reports') {
    sortConfig = {
      key: parsed.tableSort,
      direction: parsed.sortDir || 'desc',
    };
  } else if (isAgg && parsed.tab !== 'reports') {
    const sortCompare = parsed.compareScope || rateViewMode?.rateScope || 'state';
    sortConfig = defaultSort(parsed.crime, lt, sortCompare, rateViewMode)[parsed.tab];
  } else if (rateViewMode && (isCountyOrCity || isMetro)) {
    const sortCompare = parsed.compareScope || rateViewMode.rateScope;
    sortConfig = defaultSort(parsed.crime, lt, sortCompare, rateViewMode)[parsed.tab];
  } else {
    sortConfig = defaultSort(parsed.crime, lt, compareMode, rateViewMode)[parsed.tab];
  }

  // 5. Map range
  let mapRange;
  if (parsed.chart) {
    mapRange = parsed.chart;
  } else if (isAgg && parsed.tableSort && parsed.tab !== 'reports') {
    mapRange = getMapRangeFromSortKey(parsed.tableSort) || getDefaultRange(parsed.tab, lt);
  } else {
    mapRange = getDefaultRange(parsed.tab, lt);
  }

  // 6. Chart option
  let chartOption;
  if (lt) {
    const locTypeDisplay = lt.charAt(0).toUpperCase() + lt.slice(1);
    const getCompareMode = () => {
      if (!lt || (lt !== 'county' && lt !== 'city')) return null;
      if (parsed.tab === 'rates') return rateViewMode.rateScope;
      return compareMode;
    };
    let stateAbbr = null;
    if (isCountyOrCity && locationName?.includes(',')) {
      const statePart = locationName.split(',')[1]?.trim();
      stateAbbr = getStateAbbreviation(statePart);
    }
    chartOption = getChartOptionFromMapRange(
      mapRange,
      locTypeDisplay,
      parsed.crime,
      parsed.tab,
      getCrimeDisplayName(parsed.crime),
      getCompareMode,
      rateViewMode,
      stateAbbr
    );
  } else {
    chartOption = prevState.chartOption;
  }

  // 7. Pagination
  const currentPage = parsed.page || 1;
  const itemsPerPage = parsed.perPage || 20;

  return {
    selectedCrime,
    selectedDateRange,
    selectedSource,
    activeTab,
    selectedView,
    isHistorical,
    compareMode,
    rateViewMode,
    sortConfig,
    mapRange,
    chartOption,
    populationGroup,
    currentPage,
    itemsPerPage,
  };
}

export function appReducer(state, action) {
  switch (action.type) {
    case 'URL_CHANGE':
      return {
        ...state,
        ...deriveStateFromUrl(action.parsed, action.locationInfo, state),
      };
    case 'SET_CRIME':
      return { ...state, selectedCrime: action.value };
    case 'SET_DATE_RANGE':
      return { ...state, selectedDateRange: action.value };
    case 'SET_SOURCE':
      return { ...state, selectedSource: action.value };
    case 'SET_TAB':
      return { ...state, activeTab: action.value };
    case 'SET_VIEW':
      return { ...state, selectedView: action.value };
    case 'SET_HISTORICAL':
      return { ...state, isHistorical: action.value };
    case 'SET_COMPARE_MODE':
      return { ...state, compareMode: action.value };
    case 'SET_RATE_VIEW_MODE':
      return {
        ...state,
        rateViewMode: resolveRateViewValue(state, action.value),
      };
    case 'SET_MAP_RANGE':
      return { ...state, mapRange: action.value };
    case 'SET_CHART_OPTION':
      return { ...state, chartOption: action.value };
    case 'SET_POPULATION_GROUP':
      return { ...state, populationGroup: action.value };
    case 'SET_SORT':
      return { ...state, sortConfig: action.value };
    case 'BATCH_UPDATE':
      return { ...state, ...action.partial };
      case 'SET_PAGE': {
        const v = typeof action.value === 'function'
          ? action.value(state.currentPage)
          : action.value;
        return { ...state, currentPage: v };
      }
    case 'SET_ITEMS_PER_PAGE': {
      const v = typeof action.value === 'function'
        ? action.value(state.itemsPerPage)
        : action.value;
      return { ...state, itemsPerPage: v || 20, currentPage: 1 };
    }
    default:
      return state;
  }
}

export function useAppState() {
  return useReducer(appReducer, initialAppState);
}